import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import * as echarts from 'echarts';
import apiClient from '../../../stateManagers/services/axiosInterceptor';



// ==================== TYPES ====================
interface FeeRecord {
  student_id: number;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: number | null;
  paid_amount: number | null;
  fees_status: 'paid' | 'unpaid' | 'partial';
  pay_mode: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface Counts {
  total_students: number;
  paid_students: number;
  unpaid_students: number;
}

interface FeeData {
  data: FeeRecord[];
  counts: Counts;
}

// ==================== CUSTOM HOOK ====================
const useFeeData = () => {
  const [data, setData] = useState<FeeData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/students/fees-records', {
        params: {
          session: '2025-2026',
          month_year: '2025-05',
          last_paid: 'true'
        }
      });

      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to fetch fee records');
      }

      setData({ data: response.data.data, counts: response.data.counts });
    } catch (err: any) {
      console.error('Failed to fetch fee data:', err);
      setError(err.message || 'Failed to load fee data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { feeData: data, loading, error, refetch: fetchData };
};

// ==================== ECHARTS PIE CHART ====================
const EChartsPieChart: React.FC<{ data: Counts }> = ({ data }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        borderRadius: 12,
        textStyle: { 
          color: '#1e40af', 
          fontSize: 13,
          fontWeight: '600'
        },
        extraCssText: 'box-shadow: 0 8px 32px rgba(59, 130, 246, 0.2);',
        padding: [8, 12],
      },
      legend: {
        orient: 'horizontal',
        bottom: 10,
        left: 'center',
        textStyle: { 
          color: '#1e40af', 
          fontSize: 12,
          fontWeight: '600'
        },
        formatter: (name: string) => {
          let value;
          switch(name) {
            case 'Paid': value = data.paid_students; break;
            case 'Unpaid': value = data.unpaid_students; break;
            case 'Total': value = data.total_students; break;
            default: value = 0;
          }
          return `${name}: ${value}`;
        },
        itemGap: 20,
        itemWidth: 14,
        itemHeight: 14,
        icon: 'circle',
      },
      series: [{
        type: 'pie',
        radius: ['50%', '80%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: true,
        itemStyle: { 
          borderRadius: 12, 
          borderColor: '#fff', 
          borderWidth: 3,
          shadowBlur: 15,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        },
        label: {
          show: true,
          position: 'inside',
          formatter: '{c}',
          fontSize: 16,
          fontWeight: 'bold',
          color: '#fff',
          textShadowColor: 'rgba(0, 0, 0, 0.4)',
          textShadowBlur: 3,
          textShadowOffsetX: 1,
          textShadowOffsetY: 1
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 25,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
          },
          label: {
            fontSize: 18,
            fontWeight: 'bold'
          }
        },
        data: [
          { 
            value: data.paid_students, 
            name: 'Paid', 
            itemStyle: { 
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#22c55e' },
                  { offset: 1, color: '#16a34a' }
                ]
              }
            }
          },
          { 
            value: data.unpaid_students, 
            name: 'Unpaid', 
            itemStyle: { 
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#ef4444' },
                  { offset: 1, color: '#dc2626' }
                ]
              }
            }
          },
          { 
            value: data.total_students, 
            name: 'Total', 
            itemStyle: { 
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#3b82f6' },
                  { offset: 1, color: '#1d4ed8' }
                ]
              }
            }
          }
        ]
      }]
    };

    chart.setOption(option);
    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '350px' }} />;
};

// ==================== MAIN COMPONENT ====================
const FeesSummary: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const { feeData, loading, error, refetch } = useFeeData();
  const leftContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | 'auto'>('auto');

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetch]);

  useEffect(() => {
    const updateHeight = () => {
      if (leftContainerRef.current && rightContainerRef.current) {
        const leftHeight = leftContainerRef.current.getBoundingClientRect().height;
        const rightHeight = rightContainerRef.current.getBoundingClientRect().height;
        const newMaxHeight = Math.max(leftHeight, rightHeight, 350);
        setMaxHeight(newMaxHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [feeData]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'paid': return { color: '#16a34a', bgColor: '#ecfdf5', icon: 'pi-check-circle' };
      case 'unpaid': return { color: '#dc2626', bgColor: '#fef2f2', icon: 'pi-times-circle' };
      case 'partial': return { color: '#ea580c', bgColor: '#fff7ed', icon: 'pi-dollar' };
      default: return { color: '#6b7280', bgColor: '#f9fafb', icon: 'pi-times-circle' };
    }
  };

  const formatDate = (dateString: string | null) => 
    dateString ? new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // Column templates
  const studentBodyTemplate = (rowData: FeeRecord) => {
    return (
      <>
        <Tooltip target={`.student-${rowData.student_id}`} content={`Student ID: ${rowData.student_id}`} position="top" />
        <div className={`student-${rowData.student_id}`}>
          <div style={{ fontWeight: 600, color: '#1e40af', fontSize: '0.875rem' }}>{rowData.student_name}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID: {rowData.student_id}</div>
        </div>
      </>
    );
  };

  const classBodyTemplate = (rowData: FeeRecord) => {
    return (
      <div style={{ fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
        {rowData.class}{rowData.section && ` - ${rowData.section}`}
      </div>
    );
  };

  const statusBodyTemplate = (rowData: FeeRecord) => {
    const statusConfig = getStatusConfig(rowData.fees_status);
    return (
      <>
        <Tooltip 
          target={`.status-${rowData.student_id}`} 
          content={rowData.fees_status.toUpperCase()} 
          position="top"
          style={{ backgroundColor: statusConfig.color }}
        />
        <Chip
          icon={`pi ${statusConfig.icon}`}
          label={rowData.fees_status.toUpperCase()}
          className={`status-${rowData.student_id}`}
          style={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            border: `1px solid ${statusConfig.color}15`,
          }}
        />
      </>
    );
  };

  const dateBodyTemplate = (rowData: FeeRecord) => {
    return <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{formatDate(rowData.paid_date)}</div>;
  };

  // Loading State
  if (loading && !isRefreshing) {
    return (
      <div style={{
        background: 'linear-gradient(180deg, #fafbff 0%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <Card style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '2rem',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
            }}>
              <i className="pi pi-sync" style={{ 
                color: 'white', 
                fontSize: '2rem',
                animation: 'spin 1s linear infinite'
              }}></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: 700, color: '#1e40af', margin: '0 0 0.5rem 0' }}>Loading Last Paid Records</h2>
              <p style={{ color: '#64748b', margin: 0 }}>Fetching latest paid fee records...</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{
        background: 'linear-gradient(180deg, #fafbff 0%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <Card style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '2rem',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)'
            }}>
              <i className="pi pi-exclamation-circle" style={{ color: 'white', fontSize: '2rem' }}></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: 700, color: '#dc2626', margin: '0 0 0.5rem 0' }}>Unable to Load Fee Data</h2>
              <p style={{ color: '#64748b', margin: '0 0 1.5rem 0' }}>{error}</p>
              <Button
                label="Retry Loading"
                onClick={handleRefresh}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.75rem 2rem',
                  fontWeight: 600,
                  boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!feeData) return null;

  return (
    <div style={{ 
    }}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .fees-card {
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          animation: slideUp 0.6s ease-out, float 6s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .fees-card:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 12px 32px rgba(59, 130, 246, 0.12);
          background: rgba(255, 255, 255, 0.98);
        }

        .fees-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%);
        }

        .right-card {
          border-radius: 16px;
          background: linear-gradient(135deg, #f8faff 0%, #f5f7ff 100%);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          animation: slideUp 0.8s ease-out, float 6s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .right-card:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 12px 32px rgba(59, 130, 246, 0.12);
        }

        .right-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%);
        }

        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25);
          margin-bottom: 0px;
        }

        .pattern-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.04;
          pointer-events: none;
          background: url('data:image/svg+xml,<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="3" fill="none" stroke="rgba(59,130,246,0.1)" stroke-width="1"/><circle cx="30" cy="30" r="2" fill="rgba(59,130,246,0.08)"/></svg>');
        }

        .p-datatable .p-datatable-thead > tr > th {
          background-color: #f8fafc;
          font-weight: 600;
          color: #1e40af;
          font-size: 0.85rem;
          padding: 0.75rem;
        }

        .p-datatable .p-datatable-tbody > tr {
          animation: slideUp 0.5s ease-out;
        }

        .p-datatable .p-datatable-tbody > tr:hover {
          background-color: #f1f5f9;
        }

        .p-datatable .p-datatable-tbody > tr > td {
          padding: 0.75rem;
        }

        .p-card .p-card-body {
          padding: 24px;
        }

        .p-card .p-card-content {
          padding: 0;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div >
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Left Container - 70% */}
          <div style={{ flex: '0 0 68%' }} ref={leftContainerRef}>
            <Card className="fees-card" style={{ minHeight: '350px', height: maxHeight, display: 'flex', flexDirection: 'column' }}>
              <div className="pattern-overlay" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="icon-container">
                      <i className="pi pi-user" style={{ fontSize: '1rem' }}></i>
                    </div>
                    <div>
                      <h2 style={{ fontWeight: 700, color: '#1e40af', margin: 0, fontSize: '17px' }}>
                        Last Paid Fee Records
                      </h2>
                      <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
                        {feeData.data.length > 0 ? `Last ${feeData.data.length} paid entries for ${feeData.data[0]?.month_year || 'selected period'}` : 'No paid records found'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    icon={`pi pi-refresh ${isRefreshing ? 'spinning' : ''}`}
                    rounded
                    outlined
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    style={{
                      width: 35,
                      height: 35,
                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      color: '#1e40af'
                    }}
                  />
                </div>
                <div style={{ borderTop: '1px solid rgba(59, 130, 246, 0.08)', margin: '16px 0' }} />
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {feeData.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '4rem 0' }}>
                      <p style={{ color: '#64748b', fontSize: '1rem' }}>No paid fee records available</p>
                    </div>
                  ) : (
                    <DataTable value={feeData.data} stripedRows>
                      <Column field="student_name" header="Student" body={studentBodyTemplate} style={{ width: '40%' }} />
                      <Column field="class" header="Class" body={classBodyTemplate} style={{ width: '10%' }} />
                      <Column field="fees_status" header="Status" body={statusBodyTemplate} style={{ width: '20%' }} />
                      <Column field="paid_date" header="Payment Date" body={dateBodyTemplate} style={{ width: '30%' }} />
                    </DataTable>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Right Container - 30% */}
          <div style={{ flex: '0 0 30%' }} ref={rightContainerRef}>
            <Card className="right-card" style={{ minHeight: '350px', height: maxHeight, display: 'flex', flexDirection: 'column' }}>
              <div className="pattern-overlay" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="icon-container">
                      <i className="pi pi-dollar" style={{ fontSize: '1rem' }}></i>
                    </div>
                    <div>
                      <h2 style={{ fontWeight: 700, color: '#1e40af', margin: 0, fontSize: '17px' }}>
                        Payment Overview
                      </h2>
                      <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
                        Summary for {feeData.data[0]?.month_year || 'selected period'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    icon={`pi pi-refresh ${isRefreshing ? 'spinning' : ''}`}
                    rounded
                    outlined
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    style={{
                      width: 35,
                      height: 35,
                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      color: '#1e40af'
                    }}
                  />
                </div>
                <div style={{ borderTop: '1px solid rgba(59, 130, 246, 0.08)', margin: '16px 0' }} />
                <div style={{ flex: 1 }}>
                  <EChartsPieChart data={feeData.counts} />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeesSummary;